import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { getInitials, cn } from '../../lib/utils';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { 
  Plus, X, ChevronLeft, ChevronRight, Bookmark, Edit2, 
  Trash2, Check, Loader2, Image as ImageIcon 
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Highlight Circle Component for Profile
export const HighlightCircle = ({ highlight, onClick, isOwn }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center gap-1.5 shrink-0"
    data-testid={`highlight-${highlight.id}`}
  >
    <div className="p-0.5 rounded-full bg-gradient-to-br from-primary/30 via-pink-500/30 to-purple-500/30">
      <div className="p-0.5 bg-background rounded-full">
        <div className="w-16 h-16 rounded-full overflow-hidden bg-secondary">
          {highlight.cover_image ? (
            <img 
              src={highlight.cover_image} 
              alt={highlight.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Bookmark className="w-6 h-6 text-muted-foreground" />
            </div>
          )}
        </div>
      </div>
    </div>
    <span className="text-xs truncate w-16 text-center font-medium">
      {highlight.title}
    </span>
  </button>
);

// Add Highlight Button
export const AddHighlightButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center gap-1.5 shrink-0"
    data-testid="add-highlight-btn"
  >
    <div className="w-16 h-16 rounded-full bg-secondary/50 border-2 border-dashed border-primary/50 flex items-center justify-center">
      <Plus className="w-6 h-6 text-primary" />
    </div>
    <span className="text-xs font-medium">New</span>
  </button>
);

// Highlights Bar for Profile
export const HighlightsBar = ({ userId, isOwn }) => {
  const { token } = useAuth();
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showViewer, setShowViewer] = useState(false);
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchHighlights = useCallback(async () => {
    try {
      const endpoint = isOwn ? '/highlights/me' : `/highlights/user/${userId}`;
      const response = await axios.get(`${API}${endpoint}?include_stories=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHighlights(response.data);
    } catch (error) {
      console.error('Failed to fetch highlights:', error);
    } finally {
      setLoading(false);
    }
  }, [token, userId, isOwn]);

  useEffect(() => {
    fetchHighlights();
  }, [fetchHighlights]);

  const openViewer = async (highlight) => {
    try {
      const response = await axios.get(`${API}/highlights/${highlight.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedHighlight(response.data);
      setShowViewer(true);
    } catch (error) {
      console.error('Failed to fetch highlight:', error);
      toast.error('Failed to load highlight');
    }
  };

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto py-4 px-4 scrollbar-hide">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="w-16 h-20 bg-secondary/50 rounded-full animate-pulse shrink-0" />
        ))}
      </div>
    );
  }

  if (highlights.length === 0 && !isOwn) {
    return null;
  }

  return (
    <>
      <div className="flex gap-4 overflow-x-auto py-4 px-4 scrollbar-hide">
        {isOwn && <AddHighlightButton onClick={() => setShowCreateModal(true)} />}
        {highlights.map((highlight) => (
          <HighlightCircle
            key={highlight.id}
            highlight={highlight}
            isOwn={isOwn}
            onClick={() => openViewer(highlight)}
          />
        ))}
      </div>

      {showViewer && selectedHighlight && (
        <HighlightViewer
          highlight={selectedHighlight}
          isOwn={isOwn}
          onClose={() => { setShowViewer(false); setSelectedHighlight(null); fetchHighlights(); }}
        />
      )}

      {showCreateModal && (
        <CreateHighlightModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); fetchHighlights(); }}
        />
      )}
    </>
  );
};

// Highlight Viewer
export const HighlightViewer = ({ highlight, isOwn, onClose }) => {
  const { token } = useAuth();
  const [storyIndex, setStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [showEdit, setShowEdit] = useState(false);

  const stories = highlight.stories || [];
  const currentStory = stories[storyIndex];

  // Progress timer
  useEffect(() => {
    if (!currentStory) return;
    
    setProgress(0);
    const duration = currentStory.media_type === 'video' ? 15000 : 5000;
    const interval = 50;
    let elapsed = 0;

    const timer = setInterval(() => {
      elapsed += interval;
      setProgress((elapsed / duration) * 100);
      
      if (elapsed >= duration) {
        if (storyIndex < stories.length - 1) {
          setStoryIndex(storyIndex + 1);
        } else {
          onClose();
        }
      }
    }, interval);

    return () => clearInterval(timer);
  }, [storyIndex, currentStory, stories.length, onClose]);

  const nextStory = () => {
    if (storyIndex < stories.length - 1) {
      setStoryIndex(storyIndex + 1);
    } else {
      onClose();
    }
  };

  const prevStory = () => {
    if (storyIndex > 0) {
      setStoryIndex(storyIndex - 1);
    }
  };

  const removeFromHighlight = async () => {
    try {
      await axios.delete(`${API}/highlights/${highlight.id}/stories/${currentStory.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Story removed from highlight');
      if (stories.length === 1) {
        onClose();
      } else {
        nextStory();
      }
    } catch (error) {
      toast.error('Failed to remove story');
    }
  };

  if (!currentStory || stories.length === 0) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex items-center justify-center" data-testid="highlight-viewer">
        <div className="text-center text-white">
          <Bookmark className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>This highlight is empty</p>
          <Button onClick={onClose} className="mt-4">Close</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black" data-testid="highlight-viewer">
      {/* Progress bars */}
      <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 p-2">
        {stories.map((_, idx) => (
          <div key={idx} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white transition-all"
              style={{ 
                width: idx < storyIndex ? '100%' : idx === storyIndex ? `${progress}%` : '0%'
              }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="absolute top-6 left-0 right-0 z-20 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-secondary">
            {highlight.cover_image ? (
              <img src={highlight.cover_image} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Bookmark className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
          </div>
          <div>
            <p className="text-white font-semibold text-sm">{highlight.title}</p>
            <p className="text-white/70 text-xs">
              {storyIndex + 1} / {stories.length}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isOwn && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="text-white rounded-full"
                onClick={() => setShowEdit(true)}
              >
                <Edit2 className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-white rounded-full"
                onClick={removeFromHighlight}
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="text-white rounded-full"
            onClick={onClose}
          >
            <X className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {/* Story Content */}
      <div className="flex items-center justify-center h-full">
        {currentStory.media_type === 'video' ? (
          <video
            src={currentStory.media_url}
            className="max-w-full max-h-full object-contain"
            style={{ filter: currentStory.filter_style }}
            autoPlay
            playsInline
            muted
          />
        ) : (
          <img
            src={currentStory.media_url}
            alt="Highlight"
            className="max-w-full max-h-full object-contain"
            style={{ filter: currentStory.filter_style }}
          />
        )}
      </div>

      {/* Caption */}
      {currentStory.caption && (
        <div className="absolute bottom-20 left-0 right-0 text-center px-4">
          <p className="text-white text-lg font-medium bg-black/30 backdrop-blur-sm rounded-2xl px-4 py-2 inline-block">
            {currentStory.caption}
          </p>
        </div>
      )}

      {/* Navigation */}
      <button className="absolute left-0 top-0 bottom-0 w-1/3" onClick={prevStory} />
      <button className="absolute right-0 top-0 bottom-0 w-1/3" onClick={nextStory} />

      {/* Edit Modal */}
      {showEdit && (
        <EditHighlightModal
          highlight={highlight}
          onClose={() => setShowEdit(false)}
          onUpdated={onClose}
        />
      )}
    </div>
  );
};

// Create Highlight Modal
export const CreateHighlightModal = ({ open, onClose, onCreated }) => {
  const { token } = useAuth();
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [stories, setStories] = useState([]);
  const [selectedStories, setSelectedStories] = useState([]);
  const [loadingStories, setLoadingStories] = useState(true);
  const [step, setStep] = useState(1); // 1 = name, 2 = select stories

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const response = await axios.get(`${API}/stories/archivable`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStories(response.data);
      } catch (error) {
        console.error('Failed to fetch stories:', error);
      } finally {
        setLoadingStories(false);
      }
    };

    if (open) {
      fetchStories();
    }
  }, [token, open]);

  const toggleStorySelection = (storyId) => {
    setSelectedStories(prev => 
      prev.includes(storyId) 
        ? prev.filter(id => id !== storyId)
        : [...prev, storyId]
    );
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setLoading(true);
    try {
      // Create highlight
      const response = await axios.post(`${API}/highlights`, {
        title: title.trim(),
        cover_image: selectedStories.length > 0 
          ? stories.find(s => s.id === selectedStories[0])?.media_url 
          : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const highlightId = response.data.id;

      // Add selected stories
      for (const storyId of selectedStories) {
        await axios.post(`${API}/highlights/${highlightId}/stories`, {
          story_id: storyId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success('Highlight created!');
      onCreated();
    } catch (error) {
      console.error('Failed to create highlight:', error);
      toast.error('Failed to create highlight');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="create-highlight-modal">
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? 'Create Highlight' : 'Select Stories'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Create a new highlight to save your favorite stories permanently
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Highlight Name</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Vacation, Best Moments..."
                maxLength={30}
                data-testid="highlight-title-input"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={() => setStep(2)} 
                disabled={!title.trim()}
                className="flex-1"
              >
                Next
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {loadingStories ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : stories.length === 0 ? (
              <div className="text-center py-8">
                <ImageIcon className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                <p className="text-muted-foreground">No stories available</p>
                <p className="text-xs text-muted-foreground">Create stories first to add to highlights</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Select stories to add ({selectedStories.length} selected)
                </p>
                <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto">
                  {stories.map(story => (
                    <button
                      key={story.id}
                      onClick={() => toggleStorySelection(story.id)}
                      className={cn(
                        "relative aspect-square rounded-lg overflow-hidden border-2 transition-all",
                        selectedStories.includes(story.id) 
                          ? "border-primary ring-2 ring-primary/30" 
                          : "border-transparent"
                      )}
                    >
                      {story.media_type === 'video' ? (
                        <video src={story.media_url} className="w-full h-full object-cover" muted />
                      ) : (
                        <img src={story.media_url} alt="" className="w-full h-full object-cover" />
                      )}
                      {selectedStories.includes(story.id) && (
                        <div className="absolute top-1 right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                Back
              </Button>
              <Button 
                onClick={handleCreate} 
                disabled={loading}
                className="flex-1"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Edit Highlight Modal
export const EditHighlightModal = ({ highlight, onClose, onUpdated }) => {
  const { token } = useAuth();
  const [title, setTitle] = useState(highlight.title);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleUpdate = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/highlights/${highlight.id}`, {
        title: title.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Highlight updated');
      onUpdated();
    } catch (error) {
      toast.error('Failed to update highlight');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/highlights/${highlight.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Highlight deleted');
      onUpdated();
    } catch (error) {
      toast.error('Failed to delete highlight');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit Highlight</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Name</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={30}
            />
          </div>
          <div className="flex gap-2">
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={deleting}
              className="flex-1"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </Button>
            <Button 
              onClick={handleUpdate} 
              disabled={loading}
              className="flex-1"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HighlightsBar;
